// SOURCES CHIP — the provenance row under a staged draft: a few overlapping
// lettered circles and the word "Sources", expanding to the EXTERNAL pages the
// enrichment research actually opened, each a real link.
//
// IT RENDERS EXTERNAL SOURCES AND NOTHING ELSE, and that is the whole point of
// the component. A citation is a promise the reader can go and check; an
// internal provenance string ('Masonry directory import', '· Apollo
// enrichment') is not one — there is nowhere to click, because nothing was
// browsed to obtain it. Showing those strings in a chip labelled "Sources"
// was the bug this file was rewritten to kill: a row field ("LA + Santa
// Barbara + Ventura, CA", straight out of the directory's own service_area
// column) presented as though something had researched it. Row provenance is
// still told — as prose, in the story's dim lines — it simply never wears the
// costume of a citation.
//
// Every href is re-parsed here before it reaches an anchor. The component that
// renders an href is the right place for the protocol check, because it is the
// last thing between a string and a browser: readSourceLinks keeps http(s)
// only, so a `javascript:` payload loses its link rather than becoming a
// clickable one. Links open in a new tab with rel="noreferrer noopener" — the
// opened page gets no handle back to this app and no referrer.
//
// Renders NOTHING when no link survives: a researched dossier that cited no
// page shows no chip, and the story says so in words instead.
import { useId, useState } from 'react'
import { readSourceLinks } from '../lib/outreachDraft'

/**
 * @param {{href: string, host: string}[]|string[]} links  the external pages the
 *        research read — buildNarrative's `section.links` / a pass's `links`.
 *        Bare URL strings are accepted too; everything is re-validated.
 * @param {string} [name]      the lead's company name, for the aria-label
 * @param {string} [className] extra classes on the wrapper (margins)
 */
export default function SourcesChip({ links, name = '', className = '' }) {
  const [open, setOpen] = useState(false)
  const listId = useId()
  // Accept both shapes the callers hold ({href, host} objects or raw strings)
  // and re-validate: only http(s) reaches an anchor, deduped, bounded.
  const rows = readSourceLinks(
    (Array.isArray(links) ? links : []).map((l) => (typeof l === 'string' ? l : l?.href)),
  )
  if (rows.length === 0) return null
  const badges = rows.slice(0, 4)

  return (
    <div className={className}>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={listId}
        aria-label={
          name
            ? `Pages the research read for ${name} (${rows.length})`
            : `Pages the research read (${rows.length})`
        }
        title="The external pages this draft's researched fact came from"
        onClick={() => setOpen((v) => !v)}
        className="group inline-flex cursor-pointer items-center gap-x-2 rounded-full bg-transparent py-1 pl-1 pr-2.5 text-dim transition-colors [border:none] hover:bg-primary-tint hover:text-primary-dark"
      >
        {/* the overlapping lettered circles — one per DISTINCT source host */}
        <span className="flex items-center" aria-hidden="true">
          {badges.map((l, i) => (
            <span
              key={l.href}
              title={l.host}
              className="relative grid h-[18px] w-[18px] flex-none place-items-center rounded-full bg-surface text-[9.5px] font-bold uppercase leading-none text-dim [border:1px_solid_var(--border)] first:ml-0 [&:not(:first-child)]:ml-[-6px]"
              style={{ zIndex: badges.length - i }}
            >
              {l.host.charAt(0)}
            </span>
          ))}
        </span>
        <span className="text-[11.5px] font-semibold">Sources</span>
      </button>

      {open && (
        <div
          id={listId}
          className="mt-2 overflow-hidden rounded-[10px] bg-surface [border:1px_solid_var(--border)]"
        >
          {rows.map((l, i) => (
            <div
              key={l.href}
              className={`px-3 py-2 ${i > 0 ? '[border-top:1px_solid_var(--border)]' : ''}`}
            >
              <a
                href={l.href}
                target="_blank"
                rel="noreferrer noopener"
                className="text-[12.5px] font-semibold text-primary underline decoration-dotted underline-offset-2"
              >
                {l.host}
              </a>
              {/* The whole URL, so the reader can see where the link goes
                  before following it. */}
              <div className="mt-0.5 break-all text-[11px] text-faint">{l.href}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
